// Public helpers for parsing `data-luis` runtime option attributes.
// They keep hybrid and runtime flows aligned on one shared option contract.
export type LuisRuntimeWorkerMode = 'auto' | 'main' | 'worker'

export type LuisRuntimeOptions = {
  debug: boolean
  performance: boolean
  map: boolean
  lazy: boolean
  worker: LuisRuntimeWorkerMode
}

// Keep the shared default runtime option state.
export const DEFAULT_LUIS_RUNTIME_OPTIONS: LuisRuntimeOptions = {
  debug: false,
  performance: false,
  map: false,
  lazy: false,
  worker: 'auto',
}

// Parse one raw `data-luis` attribute string into an option patch.
export function parseLuisRuntimeOptionsPatchAttribute(raw: string | null): Partial<LuisRuntimeOptions> {
  // Return an empty patch when the current attribute is missing or empty.
  if (raw === null || raw.trim().length === 0) {
    // Return the empty runtime-option patch.
    return {}
  } else {
    // Continue token parsing for the current attribute text.
  }

  // Keep the mutable option patch for the current parse.
  const patch: Partial<LuisRuntimeOptions> = {}
  // Keep the normalized token list from the current attribute text.
  const tokens = raw.split(',')
  // Apply each token in declaration order.
  for (const token of tokens) {
    // Keep the normalized current token text.
    const normalized = token.trim().toLowerCase()
    // Skip empty token fragments.
    if (!normalized) {
      continue
    } else {
      // Continue token dispatch for the current runtime option token.
    }

    // Keep the negation state for the current token.
    const isNegated = normalized.startsWith('!')
    // Keep the normalized option name for the current token.
    const name = isNegated ? normalized.slice(1).trim() : normalized
    // Skip invalid option names like `!`.
    if (!name) {
      continue
    } else {
      // Continue supported option dispatch for the current token.
    }

    // Apply the current supported option token into the patch.
    if (name === 'debug' || name === 'performance' || name === 'map' || name === 'lazy') {
      patch[name] = !isNegated
    } else if (name === 'worker') {
      patch.worker = isNegated ? 'main' : 'worker'
    } else {
      // Ignore unsupported tokens for forward compatibility.
    }
  }

  // Return the parsed runtime-option patch.
  return patch
}

// Parse one raw `data-luis` attribute string into runtime options.
export function parseLuisRuntimeOptionsAttribute(raw: string | null): LuisRuntimeOptions {
  // Keep the parsed patch for the current attribute text.
  const patch = parseLuisRuntimeOptionsPatchAttribute(raw)

  // Return the normalized runtime option set.
  return { ...DEFAULT_LUIS_RUNTIME_OPTIONS, ...patch }
}

// Format one runtime-option object back into one `data-luis` attribute string.
export function stringifyLuisRuntimeOptionsAttribute(options: LuisRuntimeOptions): string {
  // Keep the ordered token list for the current runtime-option object.
  const tokens: string[] = []
  // Append the current debug token when debug logging stays enabled.
  if (options.debug) {
    tokens.push('debug')
  } else {
    // Continue token formatting when debug stays disabled.
  }
  // Append the current performance token when compile timing stays enabled.
  if (options.performance) {
    tokens.push('performance')
  } else {
    // Continue token formatting when performance stays disabled.
  }
  // Append the current map token when source-map output stays enabled.
  if (options.map) {
    tokens.push('map')
  } else {
    // Continue token formatting when map stays disabled.
  }
  // Append the current lazy token when lazy observed mode stays enabled.
  if (options.lazy) {
    tokens.push('lazy')
  } else {
    // Continue token formatting when lazy stays disabled.
  }
  // Append the current worker token when worker mode is explicit.
  if (options.worker === 'worker') {
    tokens.push('worker')
  } else if (options.worker === 'main') {
    tokens.push('!worker')
  } else {
    // Skip worker token output when current mode stays automatic.
  }

  // Return the serialized `data-luis` option string.
  return tokens.join(',')
}
